<template>
	<div class="transactions-table">
		<header>
			<div class="toolbar">
				<div ref="columnSelectorWrapperRef" class="column-selector-anchor">
					<Button
						variant="tertiary"
						size="xs"
						shape="pill"
						class="column-selector-toggle"
						@click="toggleColumnSelector"
					>
						{{ t("transactions-table.column-selector.title") }}<span class="count">{{ visibleColumnKeys.length }}</span>
					</Button>
					<ColumnSelector
						v-if="columnSelectorOpen"
						:columns="selectorColumns"
						:default-columns="defaultSelectorColumns"
						:pinned-key="PINNED_KEY"
						:group-labels="groupLabels"
						:title="t('transactions-table.column-selector.title')"
						@save="handleColumnSave"
						@close="columnSelectorOpen = false"
					/>
				</div>
				<div ref="sortWrapperRef" class="sort-wrapper">
					<Button
						variant="tertiary"
						size="xs"
						shape="pill"
						class="sort-toggle"
						:class="{ active: sortLevels.length > 0 }"
						@click="toggleSortPanel"
					>
						↕ {{ t("transactions-table.sort-button")
						}}<span v-if="sortLevels.length > 0" class="count">{{ sortLevels.length }}</span>
					</Button>
					<SortPanel
						v-if="showSortPanel"
						:options="sortOptions"
						:model-value="sortLevels"
						:max-levels="1"
						@update:model-value="handleSortLevelsUpdate"
						@close="showSortPanel = false"
					/>
				</div>
				<TransactionsTableQuickFilters ref="quickFiltersRef" class="quick-filters" @click="closeOtherPanels" />
				<div ref="filtersWrapperRef" class="filters-wrapper">
					<Button
						variant="tertiary"
						size="xs"
						shape="pill"
						class="filters-toggle"
						:class="{ active: advancedFiltersRef?.hasActiveFilters() }"
						@click="toggleFiltersPanel"
					>
						{{ t("transactions-table.other-filters-button")
						}}<span v-if="advancedFiltersRef?.activeFiltersCount" class="count">{{
							advancedFiltersRef.activeFiltersCount
						}}</span>
					</Button>
					<TableFloatingFilterPanel
						v-show="filtersPanelOpen"
						:title="t('layers.sections.transaction')"
						@close="filtersPanelOpen = false"
					>
						<template v-if="advancedFiltersRef?.hasActiveFilters()" #header-actions>
							<Button
								variant="ghost"
								size="xs"
								shape="pill"
								class="reset-filters-button"
								@click="advancedFiltersRef?.resetFilters()"
							>
								{{ t("filters.reset-to-default") }}
							</Button>
						</template>
						<TransactionsTableFilters ref="advancedFiltersRef" />
					</TableFloatingFilterPanel>
				</div>
				<TableSearch
					v-model="textSearch"
					class="search"
					:placeholder="t('transactions-table.search-placeholder')"
					@click="closeOtherPanels"
				/>
				<Button
					v-if="initialLoadComplete && hasActiveFilters"
					variant="ghost"
					size="xs"
					shape="pill"
					class="reset-filters-button"
					@click="resetAllFilters"
				>
					{{ t("filters.reset-to-default") }}
				</Button>
			</div>
			<span v-if="total !== null" class="total">
				<strong>{{ total }}</strong> {{ t("transactions-table.results-label") }}
			</span>
		</header>
		<section class="records">
			<LoadingOverlay :visible="isLoading" />
			<div class="scroll-container">
				<table :style="{ width: `max(100%, ${tableWidth}px)` }">
					<colgroup>
						<template v-for="group in sectionGroups" :key="group.key">
							<col v-for="col in group.columns" :key="col.key" :style="{ width: col.width + 'px' }" />
						</template>
					</colgroup>
					<thead>
						<tr class="section-row">
							<th v-for="group in sectionGroups" :key="group.key" :colspan="group.columns.length">
								{{ t(`transactions-table.sections.${group.key}`) }}
							</th>
						</tr>
						<tr class="columns-row">
							<template v-for="group in sectionGroups" :key="group.key">
								<th
									v-for="col in group.columns"
									:key="col.key"
									scope="col"
									:class="{ 'col-sticky': col.key === PINNED_KEY, sortable: col.sortKey != null }"
									:data-sort-order="col.sortKey ? sortOrderFor(col.sortKey) : undefined"
									@click="col.sortKey ? toggleColumnSort(col.sortKey) : undefined"
								>
									{{ columnLabel(col.key) }}
								</th>
							</template>
						</tr>
					</thead>
					<tbody>
						<tr v-if="error">
							<td colspan="100" class="empty error">{{ t("transactions-table.error") }}</td>
						</tr>
						<tr v-else-if="transactions.length === 0">
							<td colspan="100" class="empty">{{ t("transactions-table.empty") }}</td>
						</tr>
						<tr v-for="tr in transactions" :key="tr.inscriptionNumber ?? String(tr.id)">
							<template v-for="group in sectionGroups" :key="group.key">
								<td
									v-for="col in group.columns"
									:key="col.key"
									:class="{ 'col-sticky col-clickable': col.key === PINNED_KEY }"
									@click="
										col.key === PINNED_KEY
											? canEditTransactions
												? openTransactionMapTab(tr)
												: store.updateSelectedTransaction(tr)
											: undefined
									"
								>
									<template v-if="col.key === ColumnKey.RegistrationNumber">
										<span class="registration-cell">
											<span class="registration-number link-pill">
												{{ formatInscriptionNumber(tr.inscriptionNumber) }}
												<AppIcon :name="IconName.ArrowUpRight" :size="12" :stroke-width="2" />
											</span>
											<a
												v-if="tr.inscriptionNumber && !canEditTransactions"
												class="link-pill"
												:href="transactionMapUrl(tr.inscriptionNumber)"
												target="_blank"
												rel="noopener noreferrer"
												@click.stop
											>
												{{ t("opportunities.link_cta") }}
												<AppIcon :name="IconName.ArrowUpRight" :size="12" :stroke-width="2" />
											</a>
											<button
												v-if="canEditTransactions"
												type="button"
												class="edit-button"
												:title="t('transactions-table.edit-button')"
												:aria-label="t('transactions-table.edit-button')"
												@click.stop="openEditModal(tr)"
											>
												<AppIcon :name="IconName.Pencil" :size="14" :stroke-width="1.7" />
											</button>
										</span>
									</template>
									<template v-else-if="col.key === ColumnKey.Lots">
										<span v-if="dedupedLotNumbers(tr).length" class="parcels-cell">
											<span class="primary-parcel">{{ dedupedLotNumbers(tr)[0] }}</span>
											<span
												v-if="!expandedParcelsIds.has(rowKey(tr)) && dedupedLotNumbers(tr).length > 1"
												class="parcels-count"
											>
												+{{ dedupedLotNumbers(tr).length - 1 }}
											</span>
											<span
												v-if="expandedParcelsIds.has(rowKey(tr)) && dedupedLotNumbers(tr).length > 1"
												class="parcels-rest"
											>
												, {{ dedupedLotNumbers(tr).slice(1).join(", ") }}
											</span>
											<button
												v-if="dedupedLotNumbers(tr).length > 1"
												type="button"
												class="parcels-toggle"
												@click.stop="toggleParcelsExpanded(rowKey(tr))"
											>
												{{ expandedParcelsIds.has(rowKey(tr)) ? t("parcels.hide") : t("parcels.showAll") }}
											</button>
										</span>
									</template>
									<template v-else-if="col.key === ColumnKey.Address">{{
										isCoordinateAddress(tr.location.address) ? "" : tr.location.address
									}}</template>
									<template v-else-if="col.key === ColumnKey.City">{{ tr.location.municipality }}</template>
									<template v-else-if="col.key === ColumnKey.SaleDate">{{
										tr.deedOfSaleDate ? formatDateToISO(tr.deedOfSaleDate) : ""
									}}</template>
									<template v-else-if="col.key === ColumnKey.PromiseDate">{{
										tr.psaDate ? formatDateToISO(tr.psaDate) : ""
									}}</template>
									<template v-else-if="col.key === ColumnKey.SaleContext">{{ tr.saleContext }}</template>
									<template v-else-if="col.key === ColumnKey.Buyer">{{ tr.buyer?.name }}</template>
									<template v-else-if="col.key === ColumnKey.BuyerReps">{{
										[tr.buyerRepresentativeFirstName, tr.buyerRepresentativeLastName].filter(Boolean).join(" ")
									}}</template>
									<template v-else-if="col.key === ColumnKey.Seller">{{ tr.seller?.name }}</template>
									<template v-else-if="col.key === ColumnKey.SellerReps">{{
										[tr.sellerRepresentativeFirstName, tr.sellerRepresentativeLastName].filter(Boolean).join(" ")
									}}</template>
									<template v-else-if="col.key === ColumnKey.AssetType">{{
										tr.assetType
											?.filter((value): value is string => Boolean(value))
											.map(value => t(`property.assetType.${value}`))
											.join(", ")
									}}</template>
									<template v-else-if="col.key === ColumnKey.LandCategory">{{
										tr.landCategoryNew
											?.filter((value): value is string => Boolean(value))
											.map(value => t(`property.landCategory.${value}`))
											.join(", ")
									}}</template>
									<template v-else-if="col.key === ColumnKey.AuthorizedUses">{{
										tr.authorizedUses?.filter(Boolean).join(", ")
									}}</template>
									<template v-else-if="col.key === ColumnKey.CurrentState">{{ tr.currentState }}</template>
									<template v-else-if="col.key === ColumnKey.TodZone">{{
										tr.tod != null ? (tr.tod ? "✓" : "✗") : ""
									}}</template>
									<template v-else-if="col.key === ColumnKey.LandArea">{{
										formatArea(tr.superficy?.squareFeet)
									}}</template>
									<template v-else-if="col.key === ColumnKey.PurchasePrice">{{ formatPrice(tr.salePrice) }}</template>
									<template v-else-if="col.key === ColumnKey.AdjustedPrice">{{
										formatPrice(computeAdjustedPrice(tr))
									}}</template>
									<template v-else-if="col.key === ColumnKey.PricePerSqft">{{
										formatPricePerArea(tr.pricePerLandSqft)
									}}</template>
									<template v-else-if="col.key === ColumnKey.PricePerBuildableSqft">{{
										formatPricePerArea(tr.pricePerBuildableSqft)
									}}</template>
									<template v-else-if="col.key === ColumnKey.PricePerUnit">{{
										roundTo(tr.pricePerUnit, 100)
									}}</template>
									<template v-else-if="col.key === ColumnKey.CodificationType">{{ tr.codificationType }}</template>
									<template v-else-if="col.key === ColumnKey.ApprovalType">{{ tr.projectApproval }}</template>
									<template v-else-if="col.key === ColumnKey.ApprovalStatus">{{
										tr.projectApprovalAtDeedOfSale
									}}</template>
									<template v-else-if="col.key === ColumnKey.ProjectDocument">
										<a
											v-if="toExternalHref(tr.projectDocument)"
											class="link-pill"
											:href="toExternalHref(tr.projectDocument) || undefined"
											target="_blank"
											rel="noopener noreferrer"
											@click.stop
										>
											{{ t("transaction.card.openDocument") }}
											<AppIcon :name="IconName.ArrowUpRight" :size="12" :stroke-width="2" />
										</a>
									</template>
									<template v-else-if="col.key === ColumnKey.ProjectLink">
										<a
											v-if="toExternalHref(tr.projectLink)"
											class="link-pill"
											:href="toExternalHref(tr.projectLink) || undefined"
											target="_blank"
											rel="noopener noreferrer"
											@click.stop
										>
											{{ t("transaction.card.openDocument") }}
											<AppIcon :name="IconName.ArrowUpRight" :size="12" :stroke-width="2" />
										</a>
									</template>
									<template v-else-if="col.key === ColumnKey.ModelLink">
										<a
											v-if="toExternalHref(tr.volumetricImageUrl)"
											class="link-pill"
											:href="toExternalHref(tr.volumetricImageUrl) || undefined"
											target="_blank"
											rel="noopener noreferrer"
											@click.stop
										>
											{{ t("transaction.card.openDocument") }}
											<AppIcon :name="IconName.ArrowUpRight" :size="12" :stroke-width="2" />
										</a>
									</template>
									<template v-else-if="col.key === ColumnKey.LandSubcategory">{{
										tr.landSubcategory
											?.filter((value): value is string => Boolean(value))
											.map(value => t(`property.landSubcategory.${snakeToCamel(value)}`))
											.join(", ")
									}}</template>
									<template v-else-if="col.key === ColumnKey.FloorCount">{{ tr.stories }}</template>
									<template v-else-if="col.key === ColumnKey.ImplantationRate">{{
										formatDecimal(tr.siteCoverageRatio, 1)
									}}</template>
									<template v-else-if="col.key === ColumnKey.Density">{{ formatDecimal(tr.density, 1) }}</template>
									<template v-else-if="col.key === ColumnKey.BuildableArea">{{
										formatArea(tr.buildableAreaSqft)
									}}</template>
									<template v-else-if="col.key === ColumnKey.UnitCount">{{ tr.numberOfUnits }}</template>
									<template v-else-if="col.key === ColumnKey.OutdoorParking">{{ tr.parkingOutdoorCount }}</template>
									<template v-else-if="col.key === ColumnKey.IndoorParking">{{ tr.parkingIndoorCount }}</template>
									<template v-else-if="col.key === ColumnKey.TotalParking">{{ tr.parkingTotal }}</template>
									<template v-else-if="col.key === ColumnKey.ParkingRatio">{{
										formatDecimal(tr.parkingRatio, 1)
									}}</template>
									<template v-else-if="col.key === ColumnKey.ProjectComments">
										<span
											v-if="tr.projectComments"
											class="comments-cell"
											:class="{ 'is-expanded': expandedCommentsIds.has(rowKey(tr)) }"
										>
											<span class="text" v-comment-overflow="rowKey(tr)">{{ tr.projectComments }}</span>
											<button
												v-if="overflowingCommentIds.has(rowKey(tr)) || expandedCommentsIds.has(rowKey(tr))"
												type="button"
												class="toggle"
												@click.stop="toggleCommentsExpanded(rowKey(tr))"
											>
												{{ expandedCommentsIds.has(rowKey(tr)) ? t("parcels.hide") : t("parcels.showAll") }}
											</button>
										</span>
									</template>
									<template v-else-if="col.key === ColumnKey.ResidentialUnitType">{{ tr.unitType }}</template>
									<template v-else-if="col.key === ColumnKey.AvgUnitArea">{{ formatArea(tr.unitAreaSqft) }}</template>
								</td>
							</template>
						</tr>
					</tbody>
				</table>
			</div>
		</section>
		<footer class="pagination-footer">
			<ArkPagination :count="paginationCount" :page="currentPage" @page-change="handlePaginationChange" />
		</footer>
		<TransactionEditModal
			v-if="editingTransaction"
			v-model:transaction="editingTransaction"
			@close="editingTransaction = null"
		/>
	</div>
</template>

<script setup lang="ts">
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import { onClickOutside } from "@vueuse/core"
import debounce from "lodash.debounce"
import { useView } from "@/composables/useView"
import { useTransactionStore } from "@/stores/transactionStore"
import { ViewScope } from "@/types"
import { computed, onMounted, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useUnitSystem } from "@/composables/useUnitSystem"
import ArkPagination from "@/components/ui/ArkPagination.vue"
import Button from "@/components/ui/Button.vue"
import { PAGINATION_SIZE } from "@/components/constants"
import TransactionsTableFilters from "@/components/filtering/TransactionsTableFilters.vue"
import LoadingOverlay from "@/components/ui/LoadingOverlay.vue"
import TransactionEditModal from "@/components/transactions/TransactionEditModal.vue"
import { TransactionField } from "@/graphql"
import type { Transaction } from "@/model/DataModel"
import { Permission, UseRootStore } from "@/model/RootStore"
import { deepToRaw } from "@/utils"
import { formatDateToISO } from "@/utils/date"
import { snakeToCamel } from "@/utils/opportunityColumns"
import {
	formatDecimal,
	formatQuantity,
	isCoordinateAddress,
	roundTo,
	spaceOutNumbers,
	toExternalHref,
} from "@/utils/format"
import { AreaUnit } from "@/utils/unitSystem"
import ColumnSelector from "./ColumnSelector.vue"
import SortPanel from "./SortPanel.vue"
import TableFloatingFilterPanel from "./TableFloatingFilterPanel.vue"
import TableSearch from "./TableSearch.vue"
import TransactionsTableQuickFilters from "./TransactionsTableQuickFilters.vue"

const formatPrice = (value: number | null | undefined) => (value == null ? "" : formatQuantity(Math.round(value)))

const formatInscriptionNumber = (value: string | null | undefined) =>
	value == null ? "" : spaceOutNumbers(value)[0] ?? value

// Annual rate used to adjust sale prices to today's value (time-value adjustment)
// subject to change, for MVP I just took the value I saw in the template Alexis gave
// could be a manual input
const ANNUAL_PRICE_ADJUSTMENT_RATE = 0.03

// placeholder, subject to change
// adjusted price in context corresponds to the price adjusted through time
// normally the calculation depends on the psaDate, for now it'll be hardcoded
function computeAdjustedPrice(tr: Transaction): number | null {
	if (tr.salePrice == null) return null
	const date = tr.psaDate ?? tr.deedOfSaleDate
	if (date == null) return null
	const yearsElapsed = (Date.now() - date.getTime()) / (1000 * 60 * 60 * 24 * 365)
	return Math.round((tr.salePrice * Math.pow(1 + ANNUAL_PRICE_ADJUSTMENT_RATE, yearsElapsed)) / 10000) * 10000
}

const { t } = useI18n()
const store = UseRootStore()
const transactionStore = useTransactionStore()
const transactionView = useView(ViewScope.TransactionsTable)
const { areaUnitSymbol, convertAreaToPreferredUnit, convertPricePerAreaToPreferredUnit, pricePerAreaUnitSymbol } =
	useUnitSystem()

function formatArea(value: number | null | undefined): string {
	if (value == null || !Number.isFinite(value)) {
		return ""
	}

	const convertedValue = convertAreaToPreferredUnit(value, AreaUnit.SquareFeet)

	return formatQuantity(Math.round(convertedValue))
}

function formatPricePerArea(value: number | null | undefined): string {
	if (value == null || !Number.isFinite(value)) {
		return ""
	}

	const convertedValue = convertPricePerAreaToPreferredUnit(value, AreaUnit.SquareFeet)

	return formatQuantity(convertedValue, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

enum ColumnKey {
	RegistrationNumber = "registration-number",
	Lots = "lots",
	Address = "address",
	City = "city",
	SaleDate = "sale-date",
	PromiseDate = "promise-date",
	SaleContext = "sale-context",
	Buyer = "buyer",
	BuyerReps = "buyer-reps",
	Seller = "seller",
	SellerReps = "seller-reps",
	AssetType = "asset-type",
	LandCategory = "land-category",
	AuthorizedUses = "authorized-uses",
	CurrentState = "current-state",
	TodZone = "tod-zone",
	LandArea = "land-area",
	PurchasePrice = "purchase-price",
	AdjustedPrice = "adjusted-price",
	PricePerSqft = "price-per-sqft",
	PricePerBuildableSqft = "price-per-buildable-sqft",
	PricePerUnit = "price-per-unit",
	CodificationType = "codification-type",
	ApprovalType = "approval-type",
	ApprovalStatus = "approval-status",
	ProjectDocument = "project-document",
	ProjectLink = "project-link",
	ModelLink = "model-link",
	LandSubcategory = "land-subcategory",
	FloorCount = "floor-count",
	ImplantationRate = "implantation-rate",
	Density = "density",
	BuildableArea = "buildable-area",
	UnitCount = "unit-count",
	OutdoorParking = "outdoor-parking",
	IndoorParking = "indoor-parking",
	TotalParking = "total-parking",
	ParkingRatio = "parking-ratio",
	ProjectComments = "project-comments",
	ResidentialUnitType = "residential-unit-type",
	AvgUnitArea = "avg-unit-area",
}

enum SectionKey {
	Localisation = "localisation",
	DateParties = "date-parties",
	TypeTransaction = "type-transaction",
	Prix = "prix",
	Developpement = "developpement",
}

type SortKey = "city" | "saleDate" | "landArea" | "purchasePrice"

type ColumnDef = {
	key: ColumnKey
	section: SectionKey
	width: number
	sortKey?: SortKey
}

const PINNED_KEY = ColumnKey.RegistrationNumber

const COLUMN_SELECT: Record<ColumnKey, TransactionField[]> = {
	[ColumnKey.RegistrationNumber]: [TransactionField.InscriptionNumber],
	[ColumnKey.Lots]: [TransactionField.Parcels],
	[ColumnKey.Address]: [TransactionField.Address],
	[ColumnKey.City]: [TransactionField.City],
	[ColumnKey.SaleDate]: [TransactionField.DeedOfSaleDate],
	[ColumnKey.PromiseDate]: [TransactionField.PsaDate],
	[ColumnKey.SaleContext]: [TransactionField.SaleContext],
	[ColumnKey.Buyer]: [TransactionField.Buyer],
	[ColumnKey.BuyerReps]: [TransactionField.BuyerRepresentativeFirstName, TransactionField.BuyerRepresentativeLastName],
	[ColumnKey.Seller]: [TransactionField.Seller],
	[ColumnKey.SellerReps]: [
		TransactionField.SellerRepresentativeFirstName,
		TransactionField.SellerRepresentativeLastName,
	],
	[ColumnKey.AssetType]: [TransactionField.AssetType],
	[ColumnKey.LandCategory]: [TransactionField.LandCategoryNew],
	[ColumnKey.AuthorizedUses]: [TransactionField.AuthorizedUses],
	[ColumnKey.CurrentState]: [TransactionField.CurrentState],
	[ColumnKey.TodZone]: [TransactionField.Tod],
	[ColumnKey.LandArea]: [TransactionField.AreaSquareFeet],
	[ColumnKey.PurchasePrice]: [TransactionField.SalePrice],
	[ColumnKey.AdjustedPrice]: [TransactionField.AdjustedPrice],
	[ColumnKey.PricePerSqft]: [TransactionField.PricePerLandSqft],
	[ColumnKey.PricePerBuildableSqft]: [TransactionField.PricePerBuildableSqft],
	[ColumnKey.PricePerUnit]: [TransactionField.PricePerUnit],
	[ColumnKey.CodificationType]: [TransactionField.CodificationType],
	[ColumnKey.ApprovalType]: [TransactionField.ProjectApproval],
	[ColumnKey.ApprovalStatus]: [TransactionField.ProjectApprovalAtDeedOfSale],
	[ColumnKey.ProjectDocument]: [TransactionField.ProjectDocument],
	[ColumnKey.ProjectLink]: [TransactionField.ProjectLink],
	[ColumnKey.ModelLink]: [TransactionField.Project3dModelLink],
	[ColumnKey.LandSubcategory]: [TransactionField.LandSubcategory],
	[ColumnKey.FloorCount]: [TransactionField.Stories],
	[ColumnKey.ImplantationRate]: [TransactionField.SiteCoverageRatio],
	[ColumnKey.Density]: [TransactionField.Density],
	[ColumnKey.BuildableArea]: [TransactionField.BuildableAreaSqft],
	[ColumnKey.UnitCount]: [TransactionField.NumberOfUnits],
	[ColumnKey.OutdoorParking]: [TransactionField.ParkingOutdoorCount],
	[ColumnKey.IndoorParking]: [TransactionField.ParkingIndoorCount],
	[ColumnKey.TotalParking]: [TransactionField.ParkingTotal],
	[ColumnKey.ParkingRatio]: [TransactionField.ParkingRatio],
	[ColumnKey.ProjectComments]: [TransactionField.ProjectComments],
	[ColumnKey.ResidentialUnitType]: [TransactionField.UnitType],
	[ColumnKey.AvgUnitArea]: [TransactionField.UnitAreaSqft],
}

const canEditTransactions = computed(() => !!store.currentUser?.has(Permission.TransactionWrite))
const canReadSensitiveFields = !!store.currentUser?.has(Permission.TransactionSensitiveRead)
const SENSITIVE_COLUMN_KEYS = new Set<string>([ColumnKey.ModelLink, ColumnKey.ProjectComments])

const editingTransaction = ref<Transaction | null>(null)

function openEditModal(tr: Transaction): void {
	editingTransaction.value = tr
}

// styling driven from JS to prevent column sizes from changing when content changes
// e.g. when sorting the table, toggling visibility... if we want to change a column's
// size we have to change it here
const ALL_COLUMNS: ColumnDef[] = [
	{ key: ColumnKey.RegistrationNumber, section: SectionKey.Localisation, width: 220 },
	{ key: ColumnKey.Lots, section: SectionKey.Localisation, width: 160 },
	{ key: ColumnKey.Address, section: SectionKey.Localisation, width: 200 },
	{ key: ColumnKey.City, section: SectionKey.Localisation, width: 160, sortKey: "city" },

	{ key: ColumnKey.SaleDate, section: SectionKey.DateParties, width: 110, sortKey: "saleDate" },
	{ key: ColumnKey.PromiseDate, section: SectionKey.DateParties, width: 215 },
	{ key: ColumnKey.SaleContext, section: SectionKey.DateParties, width: 135 },
	{ key: ColumnKey.Buyer, section: SectionKey.DateParties, width: 170 },
	{ key: ColumnKey.BuyerReps, section: SectionKey.DateParties, width: 205 },
	{ key: ColumnKey.Seller, section: SectionKey.DateParties, width: 170 },
	{ key: ColumnKey.SellerReps, section: SectionKey.DateParties, width: 210 },

	{ key: ColumnKey.AssetType, section: SectionKey.TypeTransaction, width: 130 },
	{ key: ColumnKey.LandCategory, section: SectionKey.TypeTransaction, width: 150 },
	{ key: ColumnKey.AuthorizedUses, section: SectionKey.TypeTransaction, width: 160 },
	{ key: ColumnKey.CurrentState, section: SectionKey.TypeTransaction, width: 145 },
	{ key: ColumnKey.TodZone, section: SectionKey.TypeTransaction, width: 100 },
	{ key: ColumnKey.LandArea, section: SectionKey.TypeTransaction, width: 180, sortKey: "landArea" },

	{ key: ColumnKey.PurchasePrice, section: SectionKey.Prix, width: 150, sortKey: "purchasePrice" },
	{ key: ColumnKey.AdjustedPrice, section: SectionKey.Prix, width: 200 },
	{ key: ColumnKey.PricePerSqft, section: SectionKey.Prix, width: 120 },
	{ key: ColumnKey.PricePerBuildableSqft, section: SectionKey.Prix, width: 165 },
	{ key: ColumnKey.PricePerUnit, section: SectionKey.Prix, width: 225 },

	{ key: ColumnKey.CodificationType, section: SectionKey.Developpement, width: 170 },
	{ key: ColumnKey.ApprovalType, section: SectionKey.Developpement, width: 150 },
	{ key: ColumnKey.ApprovalStatus, section: SectionKey.Developpement, width: 290 },
	{ key: ColumnKey.ProjectDocument, section: SectionKey.Developpement, width: 170 },
	{ key: ColumnKey.ProjectLink, section: SectionKey.Developpement, width: 135 },
	{ key: ColumnKey.ModelLink, section: SectionKey.Developpement, width: 155 },
	{ key: ColumnKey.LandSubcategory, section: SectionKey.Developpement, width: 170 },
	{ key: ColumnKey.FloorCount, section: SectionKey.Developpement, width: 170 },
	{ key: ColumnKey.ImplantationRate, section: SectionKey.Developpement, width: 170 },
	{ key: ColumnKey.Density, section: SectionKey.Developpement, width: 100 },
	{ key: ColumnKey.BuildableArea, section: SectionKey.Developpement, width: 150 },
	{ key: ColumnKey.UnitCount, section: SectionKey.Developpement, width: 155 },
	{ key: ColumnKey.OutdoorParking, section: SectionKey.Developpement, width: 235 },
	{ key: ColumnKey.IndoorParking, section: SectionKey.Developpement, width: 235 },
	{ key: ColumnKey.TotalParking, section: SectionKey.Developpement, width: 205 },
	{ key: ColumnKey.ParkingRatio, section: SectionKey.Developpement, width: 150 },
	{ key: ColumnKey.ProjectComments, section: SectionKey.Developpement, width: 175 },
	{ key: ColumnKey.ResidentialUnitType, section: SectionKey.Developpement, width: 205 },
	{ key: ColumnKey.AvgUnitArea, section: SectionKey.Developpement, width: 185 },
].filter(col => canReadSensitiveFields || !SENSITIVE_COLUMN_KEYS.has(col.key))

type StoredColumn = { key: string; visible: boolean }

const defaultStoredColumns = ALL_COLUMNS.map(c => ({ key: c.key, visible: true }))

function columnFields(key: string): TransactionField[] {
	return COLUMN_SELECT[key as ColumnKey] ?? []
}

function selectedColumnFields(columns: { key: string; visible: boolean }[]): TransactionField[] {
	return [...new Set(columns.filter(column => column.visible).flatMap(column => columnFields(column.key)))]
}

function columnsFromSelectedFields(fields: TransactionField[]): StoredColumn[] {
	const selectedFields = new Set(fields)
	const visible = ALL_COLUMNS.filter(column => columnFields(column.key).every(field => selectedFields.has(field)))
		.map(column => ({
			key: column.key,
			visible: true,
			index: Math.min(...columnFields(column.key).map(field => fields.indexOf(field))),
		}))
		.sort((left, right) => left.index - right.index)
		.map(({ key, visible }) => ({ key, visible }))

	const visibleKeys = new Set(visible.map(column => column.key))
	const hidden = ALL_COLUMNS.filter(column => !visibleKeys.has(column.key)).map(column => ({
		key: column.key,
		visible: false,
	}))

	return [...visible, ...hidden]
}

// merges the persisted config with ALL_COLUMNS so newly added columns show up
// and columns removed from the code don't linger in local storage
const mergedColumns = computed<StoredColumn[]>(() => {
	const selectedColumns = transactionView.view.value.select?.transaction
	if (selectedColumns == null) return defaultStoredColumns

	return columnsFromSelectedFields(selectedColumns)
})

const visibleColumnKeys = computed<string[]>(() => mergedColumns.value.filter(c => c.visible).map(c => c.key))

const orderedColumns = computed<ColumnDef[]>(() =>
	visibleColumnKeys.value
		.map(key => ALL_COLUMNS.find(col => col.key === key))
		.filter((col): col is ColumnDef => col != null)
)

// contiguous per-section groups of the currently visible columns. Order follows orderedColumns
// (i.e. the persisted column config), so dragging a whole section in ColumnSelector reorders it
// here too. Sections with zero visible columns are dropped entirely
const sectionGroups = computed<{ key: SectionKey; columns: ColumnDef[] }[]>(() => {
	const order: SectionKey[] = []
	const bySection = new Map<SectionKey, ColumnDef[]>()
	for (const col of orderedColumns.value) {
		if (!bySection.has(col.section)) {
			order.push(col.section)
			bySection.set(col.section, [])
		}
		bySection.get(col.section)!.push(col)
	}
	return order.map(key => ({ key, columns: bySection.get(key)! }))
})

const tableWidth = computed(() => orderedColumns.value.reduce((sum, col) => sum + col.width, 0))

type SelectorColumn = { key: string; label: string; visible: boolean; group: SectionKey }

function columnLabel(key: string): string {
	switch (key) {
		case ColumnKey.LandArea:
		case ColumnKey.BuildableArea:
		case ColumnKey.AvgUnitArea:
			return `${t(`transactions-table.unit-columns.${key}`)} (${areaUnitSymbol.value})`
		case ColumnKey.PricePerSqft:
		case ColumnKey.PricePerBuildableSqft:
			return `${pricePerAreaUnitSymbol.value} ${t(`transactions-table.unit-columns.${key}`)}`
		default:
			return t(`transactions-table.columns.${key}`)
	}
}

function toSelectorColumns(cols: StoredColumn[]): SelectorColumn[] {
	return cols.map(c => ({
		...c,
		label: columnLabel(c.key),
		group: ALL_COLUMNS.find(col => col.key === c.key)!.section,
	}))
}

const selectorColumns = computed<SelectorColumn[]>(() => toSelectorColumns(mergedColumns.value))
const defaultSelectorColumns = computed<SelectorColumn[]>(() =>
	toSelectorColumns(ALL_COLUMNS.map(c => ({ key: c.key, visible: true })))
)

const groupLabels = computed<Record<string, string>>(() =>
	Object.fromEntries(Object.values(SectionKey).map(section => [section, t(`transactions-table.sections.${section}`)]))
)

function rowKey(tr: Transaction): string {
	return tr.inscriptionNumber ?? String(tr.id)
}

// opens the transaction on the map in a new tab; MapSearch.vue watches ?transaction= and fetches/selects it on load
function transactionMapUrl(inscriptionNumber: string): string {
	const url = new URL(`${window.location.origin}/`)
	url.searchParams.set("transaction", inscriptionNumber)
	return url.toString()
}

function openTransactionMapTab(tr: Transaction): void {
	if (!tr.inscriptionNumber) return
	window.open(transactionMapUrl(tr.inscriptionNumber), "_blank", "noopener,noreferrer")
}

// parcelNumbers can repeat a lot number; dedupe so the count/expanded list don't double-count it
function dedupedLotNumbers(tr: Transaction): string[] {
	return Array.from(new Set(tr.location.parcelNumbers?.filter((n): n is string => !!n) ?? []))
}

const expandedParcelsIds = ref(new Set<string>())

function toggleParcelsExpanded(id: string): void {
	const next = new Set(expandedParcelsIds.value)
	if (next.has(id)) next.delete(id)
	else next.add(id)
	expandedParcelsIds.value = next
}

const expandedCommentsIds = ref(new Set<string>())
const overflowingCommentIds = ref(new Set<string>())

function toggleCommentsExpanded(id: string): void {
	const next = new Set(expandedCommentsIds.value)
	if (next.has(id)) next.delete(id)
	else next.add(id)
	expandedCommentsIds.value = next
}

function markCommentOverflow(el: HTMLElement, id: string): void {
	const overflowing = el.scrollWidth > el.clientWidth
	if (overflowing === overflowingCommentIds.value.has(id)) return
	const next = new Set(overflowingCommentIds.value)
	if (overflowing) next.add(id)
	else next.delete(id)
	overflowingCommentIds.value = next
}

const vCommentOverflow = {
	mounted: (el: HTMLElement, binding: { value: string }) => markCommentOverflow(el, binding.value),
	updated: (el: HTMLElement, binding: { value: string }) => markCommentOverflow(el, binding.value),
}

const columnSelectorOpen = ref(false)
const showSortPanel = ref(false)

function toggleColumnSelector(): void {
	columnSelectorOpen.value = !columnSelectorOpen.value
	showSortPanel.value = false
	filtersPanelOpen.value = false
}

function toggleSortPanel(): void {
	showSortPanel.value = !showSortPanel.value
	columnSelectorOpen.value = false
	filtersPanelOpen.value = false
}

function toggleFiltersPanel(): void {
	filtersPanelOpen.value = !filtersPanelOpen.value
	columnSelectorOpen.value = false
	showSortPanel.value = false
}

function closeOtherPanels(): void {
	columnSelectorOpen.value = false
	showSortPanel.value = false
	filtersPanelOpen.value = false
}

const textSearch = ref(transactionView.view.value.filter?.transaction?.search ?? "")

const commitTextSearch = debounce((value: string) => {
	transactionView.setTransactionSearch(value)
}, 300)

watch(textSearch, value => commitTextSearch(value))

const quickFiltersRef = ref<InstanceType<typeof TransactionsTableQuickFilters> | null>(null)
const advancedFiltersRef = ref<InstanceType<typeof TransactionsTableFilters> | null>(null)

const hasActiveFilters = computed<boolean>(
	() => quickFiltersRef.value?.hasActiveFilters() === true || advancedFiltersRef.value?.hasActiveFilters() === true
)

function resetAllFilters(): void {
	commitTextSearch.cancel()
	textSearch.value = ""
	transactionView.setTransactionSearch("")
	quickFiltersRef.value?.resetFilters()
	advancedFiltersRef.value?.resetFilters()
}

function handleColumnSave(columns: { key: string; visible: boolean }[]): void {
	transactionView.setSelect({
		...transactionView.view.value.select,
		transaction: selectedColumnFields(columns),
	})
	columnSelectorOpen.value = false
}

const sortOptions = computed(() =>
	ALL_COLUMNS.filter(col => col.sortKey != null).map(col => ({
		value: col.sortKey!,
		label: columnLabel(col.key),
	}))
)

const transactions = ref<Transaction[]>([])
const total = ref<number | null>(null)
const offset = ref(0)
const isLoading = ref(false)
let latestLoadRequestId = 0
const filtersPanelOpen = ref(false)
const columnSelectorWrapperRef = ref<HTMLElement | null>(null)
const sortWrapperRef = ref<HTMLElement | null>(null)
const filtersWrapperRef = ref<HTMLElement | null>(null)
const error = ref(false)

const clickOutsideOptions = { ignore: [".popover-content", ".date-custom-panel"] }
onClickOutside(columnSelectorWrapperRef, () => (columnSelectorOpen.value = false), clickOutsideOptions)
onClickOutside(sortWrapperRef, () => (showSortPanel.value = false), clickOutsideOptions)
onClickOutside(filtersWrapperRef, () => (filtersPanelOpen.value = false), clickOutsideOptions)
const currentPage = computed({
	get: () => store.transactionsPage,
	set: page => {
		store.transactionsPage = page
	},
})
const paginationCount = computed(() => total.value ?? 0)

const activeViewOrder = computed(() => transactionView.view.value.order?.[0])
const activeSortBy = computed<SortKey | null>(() => (activeViewOrder.value?.field as SortKey | undefined) ?? null)
const activeSortOrder = computed<"asc" | "desc">(() => activeViewOrder.value?.direction ?? "desc")
const sortLevels = computed(() =>
	activeSortBy.value ? [{ key: activeSortBy.value, order: activeSortOrder.value }] : []
)

function setSort(key: SortKey | null, order: "asc" | "desc"): void {
	transactionView.setOrder(key == null ? [] : [{ field: key, direction: order }])
}

async function handleSortLevelsUpdate(levels: { key: string; order: "asc" | "desc" }[]): Promise<void> {
	const level = levels[0]
	setSort((level?.key as SortKey | undefined) ?? null, level?.order ?? "asc")
}

function sortOrderFor(key: SortKey): "asc" | "desc" | null {
	return activeSortBy.value === key ? activeSortOrder.value : null
}

async function toggleColumnSort(key: SortKey): Promise<void> {
	if (activeSortBy.value !== key) {
		setSort(key, "asc")
	} else if (activeSortOrder.value === "asc") {
		setSort(key, "desc")
	} else {
		setSort(null, "desc")
	}
}

function getTransactionsPageCacheKey(page: number): string {
	const sortKey = activeSortBy.value ?? null
	const sortDirection = activeSortBy.value ? activeSortOrder.value : null
	const key = {
		filters: deepToRaw(transactionView.view.value),
		limit: PAGINATION_SIZE,
		page,
		sortBy: sortKey,
		sortOrder: sortDirection,
	}

	return JSON.stringify(key)
}

function loadTransactionsPageCache(cacheKey: string): boolean {
	const cache = store.transactionsPageCache

	if (cache?.key !== cacheKey) {
		return false
	}

	transactions.value = cache.items
	offset.value = cache.offset
	total.value = cache.total
	error.value = false

	return true
}

type PaginationChangeDetails = {
	page: number
	pageSize: number
	offset: number
}

async function loadPage(page = currentPage.value): Promise<void> {
	const cacheKey = getTransactionsPageCacheKey(page)
	const requestId = ++latestLoadRequestId

	if (loadTransactionsPageCache(cacheKey)) {
		isLoading.value = false
		return
	}

	isLoading.value = true
	error.value = false

	try {
		const nextOffset = (page - 1) * PAGINATION_SIZE
		const { items, total: fetchedTotal } = await transactionStore.fetchByView(transactionView.view.value, {
			limit: PAGINATION_SIZE,
			offset: nextOffset,
		})

		if (requestId !== latestLoadRequestId) return

		transactions.value = items
		offset.value = nextOffset
		total.value = fetchedTotal
		store.transactionsPageCache = {
			key: cacheKey,
			items,
			offset: nextOffset,
			total: fetchedTotal,
		}
	} catch (e) {
		if (requestId !== latestLoadRequestId) return
		error.value = true
		console.error(e)
	} finally {
		if (requestId === latestLoadRequestId) isLoading.value = false
	}
}

async function reset(): Promise<void> {
	currentPage.value = 1
	offset.value = 0
	total.value = null

	await loadPage(1)
}

async function handlePaginationChange(details: PaginationChangeDetails): Promise<void> {
	currentPage.value = details.page
	offset.value = details.offset

	await loadPage(details.page)
}

watch(paginationCount, count => {
	const pageCount = Math.max(Math.ceil(count / PAGINATION_SIZE), 1)

	if (currentPage.value <= pageCount) {
		return
	}

	currentPage.value = pageCount
})

watch(
	() => transactionView.view.value,
	async () => {
		store.transactionsPageCache = null
		await reset()
	},
	{ deep: true }
)

// onMounted runs loadPage(), and the deep view watcher can fire a follow-up
// reset() while the filter state finishes hydrating. Only reveal the reset button once
// loading has settled, so it doesn't flash while filters are still stabilising.
const initialLoadComplete = ref(false)

function revealResetButtonWhenLoadSettles(): void {
	setTimeout(() => {
		if (!isLoading.value) initialLoadComplete.value = true
	}, 250)
}

watch(isLoading, loading => {
	if (!loading && !initialLoadComplete.value) revealResetButtonWhenLoadSettles()
})

onMounted(async () => {
	await loadPage()
	revealResetButtonWhenLoadSettles()
})
</script>

<style scoped>
.reset-filters-button {
	white-space: nowrap;
	align-self: center;
	line-height: 1;
}

.transactions-table {
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;

	header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		background-color: var(--neutral-0);
		padding: var(--spacing-sm) var(--spacing-xs);
		margin-bottom: var(--spacing-xs);
		border-radius: var(--border-radius-lg);
		gap: var(--spacing-md);
		color: var(--neutral-900);
		font-weight: var(--font-weight-medium);

		.toolbar {
			display: flex;
			align-items: center;
			gap: var(--spacing-xs);

			.search {
				margin-left: auto;
			}
		}

		.column-selector-anchor,
		.sort-wrapper,
		.filters-wrapper {
			position: relative;
		}

		.filters-toggle,
		.sort-toggle {
			&.active {
				border-color: var(--neutral-900);
			}
		}

		.filters-toggle,
		.sort-toggle,
		.column-selector-toggle {
			.count {
				display: inline-flex;
				align-items: center;
				justify-content: center;
				min-width: var(--space-16);
				height: var(--space-16);
				margin-left: var(--spacing-xs);
				padding: 0 var(--spacing-xxs);
				border-radius: var(--border-radius-full);
				background: var(--neutral-200);
				color: var(--neutral-900);
				font-size: var(--font-size-xxs);
				font-weight: var(--font-weight-semibold);
			}
		}

		.quick-filters {
			flex-shrink: 0;
		}

		.total {
			flex-shrink: 0;
			white-space: nowrap;
			font-size: var(--font-size-small);
			color: var(--neutral-600);

			strong {
				font-weight: var(--font-weight-semibold);
			}
		}
	}

	.records {
		position: relative;
		flex: 1;
		min-height: 0;

		.scroll-container {
			height: 100%;
			overflow: auto;
			border: var(--border-width-thin) solid var(--neutral-200);
			border-radius: var(--border-radius-lg);

			table {
				background-color: var(--neutral-0);
				table-layout: fixed;
				font-size: var(--font-size-small);
				border-collapse: separate;
				border-spacing: 0;

				:is(thead, tbody) tr :is(th, td) {
					padding: var(--space-10);
					border-bottom: var(--border-width-thin) solid var(--neutral-100);
				}

				thead tr.section-row th {
					position: sticky;
					top: 0;
					z-index: 2;
					height: var(--space-40);
					padding: var(--space-10);
					background-color: var(--neutral-50);
					color: var(--neutral-600);
					font-size: var(--font-size-xs);
					font-weight: var(--font-weight-medium);
					text-align: left;
					text-transform: uppercase;
					letter-spacing: 0.02em;
					white-space: nowrap;
					border-bottom: var(--border-width-thin) solid var(--neutral-200);
				}

				thead tr.columns-row th {
					position: sticky;
					top: var(--space-40);
					z-index: 1;
					padding: var(--space-10);

					&.col-sticky {
						left: 0;
						z-index: 2;
						will-change: transform;
						box-shadow: inset -0.5px 0 0 var(--neutral-200);
					}
					font-size: var(--font-size-xs);
					font-weight: var(--font-weight-medium);
					text-align: left;
					text-transform: uppercase;
					letter-spacing: 0.02em;
					background-color: var(--neutral-0);
					color: var(--neutral-600);
					white-space: nowrap;
					border-bottom: var(--border-width-thin) solid var(--neutral-200);

					&.sortable {
						cursor: pointer;
						user-select: none;

						&::after {
							content: " ↕";
							opacity: 0.4;
						}

						&:hover::after {
							opacity: 0.7;
						}

						&[data-sort-order="asc"]::after {
							content: " ↑";
							opacity: 0.8;
						}

						&[data-sort-order="desc"]::after {
							content: " ↓";
							opacity: 0.8;
						}
					}
				}

				tbody {
					background-color: var(--neutral-0);

					tr:hover {
						background-color: var(--neutral-50);

						td.col-sticky {
							background-color: var(--neutral-50);
						}
					}

					td {
						text-align: left;
						white-space: normal;
						overflow: hidden;
						text-overflow: ellipsis;

						&:empty::after {
							content: "—";
							color: var(--neutral-400);
						}
					}

					td.col-sticky {
						position: sticky;
						left: 0;
						background-color: var(--neutral-0);
						z-index: 1;
						box-shadow: inset -0.5px 0 0 var(--neutral-200);
					}

					.edit-button {
						display: inline-flex;
						align-items: center;
						justify-content: center;
						flex-shrink: 0;
						width: var(--spacing-lg);
						height: var(--spacing-lg);
						margin-left: auto;
						padding: 0;
						border: var(--border-width-thin) solid var(--neutral-200);
						border-radius: var(--border-radius-sm);
						background: none;
						color: var(--neutral-600);
						cursor: pointer;

						&:hover {
							background-color: var(--neutral-100);
							border-color: var(--neutral-400);
							color: var(--neutral-900);
						}
					}

					td.lots {
						overflow: visible;
					}

					.registration-cell {
						display: flex;
						align-items: center;
						gap: var(--spacing-sm);
					}

					.link-pill {
						display: inline-flex;
						align-items: center;
						gap: var(--spacing-xxs);
						padding: var(--spacing-xxs) var(--spacing-sm);
						border-radius: var(--border-radius-full);
						background-color: var(--neutral-100);
						color: var(--neutral-700);
						font-size: var(--font-size-xs);
						font-weight: var(--font-weight-medium);
						text-decoration: none;
						flex-shrink: 0;
						transition:
							background-color 0.2s ease,
							color 0.2s ease;

						&:hover {
							background-color: var(--neutral-200);
							color: var(--neutral-900);
						}
					}

					.comments-cell {
						display: flex;
						align-items: baseline;
						gap: var(--space-4);
						width: 100%;

						.text {
							flex: 1;
							min-width: 0;
							overflow: hidden;
							text-overflow: ellipsis;
							white-space: nowrap;
						}

						&.is-expanded .text {
							white-space: normal;
						}

						.toggle {
							flex-shrink: 0;
							background: none;
							border: none;
							padding: 0;
							font: inherit;
							font-size: var(--font-size-xxs);
							font-weight: var(--font-weight-semibold);
							color: var(--neutral-600);
							cursor: pointer;

							&:hover {
								color: var(--primary-green);
							}
						}
					}

					.parcels-cell {
						display: inline;

						.parcels-count {
							font-size: var(--font-size-xxs);
							color: var(--neutral-600);
						}

						.parcels-toggle {
							display: inline;
							margin-left: var(--space-4);
							background: none;
							border: none;
							padding: 0;
							font: inherit;
							font-size: var(--font-size-xxs);
							font-weight: var(--font-weight-semibold);
							color: var(--neutral-600);
							cursor: pointer;

							&:hover {
								color: var(--primary-green);
							}
						}
					}

					.registration-number {
						font-variant-numeric: tabular-nums;
						letter-spacing: 0.02em;

						.app-icon {
							flex-shrink: 0;
						}
					}

					td.col-clickable {
						cursor: pointer;

						.registration-number:not(.link-pill) {
							color: var(--blue-dark);
							text-decoration: underline;
						}

						&:hover .registration-number:not(.link-pill) {
							color: var(--neutral-900);
						}
					}

					td.empty {
						text-align: center;
						color: var(--neutral-600);
						padding: var(--spacing-lg);
						white-space: normal;

						&.error {
							color: var(--accent-warning);
						}
					}
				}
			}
		}
	}

	.pagination-footer {
		display: flex;
		flex-shrink: 0;
		padding: var(--spacing-md) var(--spacing-xs) 0;
	}
}
</style>
